import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";
import Icon from "@material-ui/core/Icon";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Card from "components/Card/Card.jsx";
import CardBody from "components/Card/CardBody.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const ENTRY_TYPES = ["Lecture", "Tutorial", "Lab", "Practical"];
const TYPE_TONE = {
  Lecture: "info",
  Tutorial: "success",
  Lab: "warning",
  Practical: "danger"
};

const BLANK_FORM = {
  day: "Monday",
  startTime: "",
  endTime: "",
  programmeId: "",
  courseId: "",
  staffId: "",
  venue: "",
  type: "Lecture",
  semesterId: ""
};

const styles = {
  container: {
    padding: "30px",
    background: "#f8fafc",
    minHeight: "100vh"
  },
  headerRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: "32px"
  },
  titleArea: {
    display: "flex",
    flexDirection: "column",
    gap: "8px"
  },
  pageTitle: {
    fontSize: "32px",
    fontWeight: 700,
    color: "#0f172a",
    margin: 0,
    letterSpacing: "-0.02em"
  },
  pageSubtitle: {
    fontSize: "15px",
    color: "#64748b",
    margin: 0
  },
  headerActions: {
    display: "flex",
    gap: "12px"
  },
  
  // KPI Cards
  kpiRow: {
    display: "grid",
    gridTemplateColumns: "repeat(5, 1fr)",
    gap: "20px",
    marginBottom: "32px"
  },
  kpiCard: {
    background: "#fff",
    padding: "24px",
    borderRadius: "16px",
    border: "1px solid rgba(0,0,0,0.05)",
    boxShadow: "0 1px 3px rgba(0,0,0,0.02)",
    display: "flex",
    gap: "16px",
    alignItems: "flex-start"
  },
  kpiIconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px"
  },
  kpiContent: {
    display: "flex",
    flexDirection: "column",
    gap: "4px"
  },
  kpiValue: {
    fontSize: "24px",
    fontWeight: 700,
    color: "#0f172a",
    lineHeight: 1
  },
  kpiLabel: {
    fontSize: "13px",
    fontWeight: 500,
    color: "#64748b"
  },
  kpiTrend: {
    fontSize: "12px",
    color: "#94a3b8",
    marginTop: "4px"
  },

  // Filters
  filterToolbar: {
    background: "#fff",
    padding: "16px",
    borderRadius: "12px",
    border: "1px solid rgba(0,0,0,0.05)",
    marginBottom: "12px",
    display: "flex",
    flexDirection: "column",
    gap: "16px"
  },
  filterMainRow: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "nowrap"
  },
  searchBox: {
    position: "relative",
    flex: 1,
    minWidth: "200px"
  },
  searchIcon: {
    position: "absolute",
    left: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    color: "#64748b",
    fontSize: "20px"
  },
  searchInput: {
    width: "100%",
    padding: "10px 12px 10px 40px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    fontSize: "14px",
    background: "#fff",
    "&:focus": {
      outline: "none",
      borderColor: "#3b82f6",
      boxShadow: "0 0 0 3px rgba(59, 130, 246, 0.1)"
    }
  },
  filterControl: {
    minWidth: "160px"
  },
  filterInputLabel: {
    fontSize: "12px",
    fontWeight: 600,
    color: "#64748b",
    marginBottom: "4px",
    position: "relative",
    transform: "none"
  },
  filterSelect: {
    "& .MuiSelect-select": {
      padding: "10px 12px",
      fontSize: "14px"
    },
    background: "#fff",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    "&::before, &::after": { display: "none" }
  },
  moreFiltersBtn: {
    padding: "10px 16px",
    borderRadius: "8px",
    fontSize: "14px",
    fontWeight: 500,
    color: "#4f46e5",
    background: "#f5f3ff",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    whiteSpace: "nowrap",
    "&:hover": { background: "#ede9fe" }
  },
  tagRow: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flexWrap: "wrap"
  },
  filterTag: {
    padding: "4px 12px",
    borderRadius: "999px",
    background: "#eff6ff",
    color: "#2563eb",
    fontSize: "12px",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  clearAll: {
    fontSize: "12px",
    color: "#64748b",
    cursor: "pointer",
    marginLeft: "8px",
    "&:hover": { color: "#0f172a" }
  },
  toolbarRight: {
    display: "flex",
    alignItems: "center",
    gap: "8px"
  },
  toggleBtnGroup: {
    display: "flex",
    background: "#f1f5f9",
    padding: "2px",
    borderRadius: "8px"
  },
  toggleBtn: {
    padding: "8px 16px",
    borderRadius: "6px",
    fontSize: "13px",
    fontWeight: 500,
    cursor: "pointer",
    border: "none",
    display: "flex",
    alignItems: "center",
    gap: "6px"
  },
  toggleBtnActive: {
    background: "#fff",
    color: "#0f172a",
    boxShadow: "0 1px 2px rgba(0,0,0,0.05)"
  },
  toggleBtnInactive: {
    background: "transparent",
    color: "#64748b"
  },
  actionBtn: {
    padding: "10px 16px",
    borderRadius: "8px",
    border: "1px solid #e2e8f0",
    background: "#fff",
    fontSize: "14px",
    fontWeight: 500,
    display: "flex",
    alignItems: "center",
    gap: "8px",
    cursor: "pointer"
  },

  errorMsg: { color: "#f44336", fontSize: "13px" },
  formControl: {
    width: "100%",
    marginTop: "16px",
    "& .MuiInputLabel-root": {
      whiteSpace: "nowrap",
      minWidth: "fit-content"
    }
  }
};

const BLANK_GENERATE_FORM = {
  programmeId: "",
  semesterId: "",
  venues: "Room 1, Room 2, Room 3",
  dayStart: 8,
  dayEnd: 17
};

class TimetableBuilder extends Component {
  state = {
    entries: [],
    semesters: [],
    courses: [],
    staff: [],
    programmes: [],
    selectedSemester: "",
    selectedProgramme: "",
    loading: true,
    open: false,
    editing: null,
    form: { ...BLANK_FORM },
    saving: false,
    error: null,
    formError: null,
    confirmDeleteId: null,
    generateOpen: false,
    generateForm: { ...BLANK_GENERATE_FORM },
    generating: false,
    generateError: null,
    generatePreview: null
  };

  componentDidMount() {
    this.fetchSemesters();
    this.fetchCourses();
    this.fetchStaff();
    this.fetchProgrammes();
  }

  fetchSemesters = async () => {
    try {
      const res = await api.get("/semesters");
      const d = res.data.data || res.data;
      const list = Array.isArray(d) ? d : d.data || [];
      const current = list.find(s => s.isCurrent) || list[0];
      this.setState(
        { semesters: list, selectedSemester: current ? String(current.id) : "" },
        this.fetchEntries
      );
    } catch (e) {
      this.fetchEntries();
    }
  };

  fetchEntries = async () => {
    this.setState({ loading: true, error: null });
    try {
      const params = {};
      if (this.state.selectedSemester) params.semesterId = this.state.selectedSemester;
      if (this.state.selectedProgramme) params.programmeId = this.state.selectedProgramme;
      const res = await api.get("/timetable", { params });
      const d = res.data.data || res.data;
      this.setState({ entries: Array.isArray(d) ? d : d.data || [], loading: false });
    } catch (e) {
      this.setState({ entries: [], loading: false, error: "Could not load timetable entries." });
    }
  };

  fetchCourses = async () => {
    try {
      const res = await api.get("/courses");
      const d = res.data.data || res.data;
      this.setState({ courses: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  fetchStaff = async () => {
    try {
      const res = await api.get("/staff");
      const d = res.data.data || res.data;
      this.setState({ staff: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  fetchProgrammes = async () => {
    try {
      const res = await api.get("/programmes", { params: { limit: 100 } });
      const d = res.data.data || res.data;
      this.setState({ programmes: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* silent */ }
  };

  handleProgrammeFilter = e => {
    this.setState({ selectedProgramme: e.target.value }, this.fetchEntries);
  };

  handleChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ form: { ...prev.form, [field]: v } }));
  };

  handleFormProgrammeChange = e => {
    const v = e.target.value;
    this.setState(prev => {
      const stillValid = prev.courses.find(
        c => String(c.id) === String(prev.form.courseId) && String(c.programmeId) === String(v)
      );
      return { form: { ...prev.form, programmeId: v, courseId: stillValid ? prev.form.courseId : "" } };
    });
  };

  handleSemesterFilter = e => {
    this.setState({ selectedSemester: e.target.value }, this.fetchEntries);
  };

  openAdd = () => {
    const { selectedSemester, selectedProgramme } = this.state;
    this.setState({
      open: true,
      editing: null,
      form: { ...BLANK_FORM, semesterId: selectedSemester, programmeId: selectedProgramme },
      formError: null
    });
  };

  openEdit = entry => {
    const course = this.state.courses.find(c => String(c.id) === String(entry.courseId));
    this.setState({
      open: true,
      editing: entry.id,
      formError: null,
      form: {
        day: entry.dayOfWeek || entry.day || "Monday",
        startTime: entry.startTime || "",
        endTime: entry.endTime || "",
        programmeId: entry.course?.programmeId || (course ? String(course.programmeId) : ""),
        courseId: entry.courseId ? String(entry.courseId) : "",
        staffId: entry.staffId ? String(entry.staffId) : "",
        venue: entry.venue || "",
        type: entry.type || "Lecture",
        semesterId: entry.semesterId ? String(entry.semesterId) : ""
      }
    });
  };

  handleSave = async () => {
    const { form, editing } = this.state;
    if (!form.day || !form.startTime || !form.endTime || !form.courseId) {
      this.setState({ formError: "Day, start/end time, and course are required." });
      return;
    }
    this.setState({ saving: true, formError: null });
    const { programmeId, ...payload } = form;
    try {
      if (editing) {
        await api.put(`/timetable/${editing}`, payload);
      } else {
        await api.post("/timetable", payload);
      }
      this.setState({ open: false, saving: false });
      this.fetchEntries();
    } catch (e) {
      this.setState({ formError: e.response?.data?.message || "Failed to save.", saving: false });
    }
  };

  handleDelete = async id => {
    this.setState({ confirmDeleteId: null });
    try {
      await api.delete(`/timetable/${id}`);
      this.fetchEntries();
    } catch (e) {
      this.setState({ error: "Failed to delete entry." });
    }
  };

  handleGenerateChange = field => e => {
    const v = e.target.value;
    this.setState(prev => ({ generateForm: { ...prev.generateForm, [field]: v } }));
  };

  openGenerate = () => {
    const { selectedSemester, selectedProgramme } = this.state;
    this.setState({
      generateOpen: true,
      generateError: null,
      generatePreview: null,
      generateForm: { ...BLANK_GENERATE_FORM, programmeId: selectedProgramme, semesterId: selectedSemester }
    });
  };

  runGenerate = async commit => {
    const { generateForm } = this.state;
    if (!generateForm.programmeId || !generateForm.semesterId) {
      this.setState({ generateError: "Programme and semester are required." });
      return;
    }
    this.setState({ generating: true, generateError: null });
    try {
      const venues = generateForm.venues.split(",").map(v => v.trim()).filter(Boolean);
      const res = await api.post("/timetable/auto-generate", {
        programmeId: generateForm.programmeId,
        semesterId: generateForm.semesterId,
        venues,
        dayStart: Number(generateForm.dayStart),
        dayEnd: Number(generateForm.dayEnd),
        commit
      });
      const d = res.data.data || res.data;
      if (commit) {
        this.setState({ generating: false, generateOpen: false, generatePreview: null });
        this.fetchEntries();
      } else {
        this.setState({
          generating: false,
          generatePreview: {
            entries: d,
            unplaced: res.data.unplaced || [],
            alreadyScheduled: res.data.alreadyScheduled || []
          }
        });
      }
    } catch (e) {
      this.setState({ generateError: e.response?.data?.message || "Auto-generation failed.", generating: false });
    }
  };

  getCourseLabel = id => {
    const { courses } = this.state;
    const c = courses.find(x => String(x.id) === String(id));
    return c ? (c.code ? `${c.code} — ${c.name}` : c.name) : id || "—";
  };

  getStaffLabel = id => {
    const { staff } = this.state;
    const s = staff.find(x => String(x.id) === String(id));
    return s ? `${s.firstName || ""} ${s.lastName || ""}`.trim() : id || "—";
  };

  onFilterClick = (show) => {
    // Already handled by DataTable internal state to toggle toolbar
  };
  onSortClick = (event) => {
    // Can implement a sort menu if needed
  };
  onViewClick = (density) => {
    // Density handled internally by DataTable
  };

  render() {
    const { classes } = this.props;
    const {
      entries, semesters, courses, staff, programmes,
      selectedSemester, selectedProgramme, loading, open, editing, form,
      saving, error, formError, confirmDeleteId,
      generateOpen, generateForm, generating, generateError, generatePreview
    } = this.state;

    const dialogCourses = form.programmeId
      ? courses.filter(c => String(c.programmeId) === String(form.programmeId))
      : courses;

    const totalEntries = entries.length;
    const uniqueCourses = new Set(entries.map(e => e.courseId)).size;
    const uniqueLecturers = new Set(entries.map(e => e.staffId).filter(Boolean)).size;
    const uniqueVenues = new Set(entries.map(e => e.venue).filter(Boolean)).size;
    const conflicts = 0; // Placeholder for now

    const columns = [
      {
        id: "select",
        header: ({ table }) => (
          <input
            type="checkbox"
            checked={table.getIsAllRowsSelected()}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 40
      },
      {
        id: "day_time",
        header: "Day / Time",
        cell: info => (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <span style={{ fontWeight: 600, color: "#0f172a" }}>{info.row.original.dayOfWeek || info.row.original.day}</span>
            <span style={{ fontSize: "12px", color: "#64748b" }}>{info.row.original.startTime} - {info.row.original.endTime}</span>
          </div>
        )
      },
      {
        id: "course",
        header: "Course Name / Programme",
        cell: info => {
          const entry = info.row.original;
          const course = entry.course || courses.find(c => String(c.id) === String(entry.courseId));
          const prog = programmes.find(p => String(p.id) === String(course?.programmeId));
          return (
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span style={{ fontWeight: 600, color: "#0f172a" }}>{course?.name || this.getCourseLabel(entry.courseId)}</span>
              <span style={{ fontSize: "12px", color: "#64748b" }}>{prog?.name || "—"}</span>
            </div>
          );
        }
      },
      {
        id: "lecturer",
        header: "Lecturer",
        cell: info => {
          const entry = info.row.original;
          const name = entry.staff ? `${entry.staff.firstName || ""} ${entry.staff.lastName || ""}`.trim() : this.getStaffLabel(entry.staffId);
          return <span style={{ fontWeight: 500 }}>{name}</span>;
        }
      },
      {
        id: "venue",
        header: "Venue",
        cell: info => (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#64748b" }}>
            <Icon style={{ fontSize: "16px" }}>location_on</Icon>
            {info.row.original.venue || "—"}
          </div>
        )
      },
      {
        id: "type",
        header: "Type",
        cell: info => {
          const type = info.row.original.type || "Lecture";
          return (
            <span style={{
              padding: "4px 12px",
              borderRadius: "6px",
              fontSize: "12px",
              fontWeight: 500,
              background: "#eff6ff",
              color: "#2563eb"
            }}>
              {type}
            </span>
          );
        }
      },
      {
        id: "status",
        header: "Status",
        cell: () => (
          <div style={{ display: "flex", alignItems: "center", gap: "6px", color: "#10b981", fontSize: "12px", fontWeight: 600 }}>
            <div style={{ width: "6px", height: "6px", borderRadius: "50%", background: "#10b981" }} />
            Scheduled
          </div>
        )
      },
      {
        id: "actions",
        header: "Actions",
        meta: { align: "right" },
        cell: info => (
          <div style={{ display: "flex", gap: "12px", justifyContent: "flex-end", color: "#64748b" }}>
            <Icon style={{ cursor: "pointer", fontSize: "20px" }}>visibility</Icon>
            <Icon style={{ cursor: "pointer", fontSize: "20px" }} onClick={() => this.openEdit(info.row.original)}>edit</Icon>
            <Icon style={{ cursor: "pointer", fontSize: "20px", color: "#ef4444" }} onClick={() => this.setState({ confirmDeleteId: info.row.original.id })}>delete</Icon>
          </div>
        )
      }
    ];

    const currentProg = programmes.find(p => String(p.id) === String(selectedProgramme));
    const currentSem = semesters.find(s => String(s.id) === String(selectedSemester));

    return (
      <div className={classes.container}>
        <div className={classes.headerRow}>
          <div className={classes.titleArea}>
            <h1 className={classes.pageTitle}>Manage Timetable</h1>
            <p className={classes.pageSubtitle}>Build and organize class schedules, venues, and lecturer assignments.</p>
          </div>
          <div className={classes.headerActions}>
            <button className={classes.actionBtn} onClick={this.openGenerate}>
              <Icon style={{ fontSize: "20px" }}>auto_awesome</Icon>
              Auto-Generate
            </button>
            <Button color="primary" onClick={this.openAdd} style={{ borderRadius: "8px", textTransform: "none", fontWeight: 600, padding: "10px 20px" }}>
              <Icon style={{ marginRight: "8px" }}>add</Icon> Add Session
            </Button>
          </div>
        </div>

        <div className={classes.kpiRow}>
          <div className={classes.kpiCard}>
            <div className={`${classes.kpiIconBox}`} style={{ background: "#f5f3ff", color: "#4f46e5" }}>
              <Icon>calendar_today</Icon>
            </div>
            <div className={classes.kpiContent}>
              <span className={classes.kpiValue}>{totalEntries}</span>
              <span className={classes.kpiLabel}>Scheduled Sessions</span>
              <span className={classes.kpiTrend}>Today: {totalEntries}</span>
            </div>
          </div>
          <div className={classes.kpiCard}>
            <div className={`${classes.kpiIconBox}`} style={{ background: "#eff6ff", color: "#2563eb" }}>
              <Icon>menu_book</Icon>
            </div>
            <div className={classes.kpiContent}>
              <span className={classes.kpiValue}>{uniqueCourses}</span>
              <span className={classes.kpiLabel}>Total Courses</span>
              <span className={classes.kpiTrend}>This semester</span>
            </div>
          </div>
          <div className={classes.kpiCard}>
            <div className={`${classes.kpiIconBox}`} style={{ background: "#f0fdf4", color: "#16a34a" }}>
              <Icon>group</Icon>
            </div>
            <div className={classes.kpiContent}>
              <span className={classes.kpiValue}>{uniqueLecturers}</span>
              <span className={classes.kpiLabel}>Active Lecturers</span>
              <span className={classes.kpiTrend}>This semester</span>
            </div>
          </div>
          <div className={classes.kpiCard}>
            <div className={`${classes.kpiIconBox}`} style={{ background: "#fffbeb", color: "#d97706" }}>
              <Icon>corporate_fare</Icon>
            </div>
            <div className={classes.kpiContent}>
              <span className={classes.kpiValue}>{uniqueVenues}</span>
              <span className={classes.kpiLabel}>Venues</span>
              <span className={classes.kpiTrend}>Available</span>
            </div>
          </div>
          <div className={classes.kpiCard}>
            <div className={`${classes.kpiIconBox}`} style={{ background: "#fef2f2", color: "#dc2626" }}>
              <Icon>check_circle</Icon>
            </div>
            <div className={classes.kpiContent}>
              <span className={classes.kpiValue}>{conflicts}</span>
              <span className={classes.kpiLabel}>Conflicts</span>
              <span className={classes.kpiTrend}>All clear</span>
            </div>
          </div>
        </div>

        <div className={classes.filterToolbar}>
          <div className={classes.filterMainRow}>
            <div className={classes.searchBox}>
              <Icon className={classes.searchIcon}>search</Icon>
              <input
                type="text"
                className={classes.searchInput}
                placeholder="Search sessions..."
                value={this.state.searchValue || ""}
                onChange={e => this.setState({ searchValue: e.target.value })}
              />
            </div>

            <FormControl className={classes.filterControl}>
              <InputLabel className={classes.filterInputLabel}>Programme</InputLabel>
              <Select
                value={selectedProgramme}
                onChange={this.handleProgrammeFilter}
                className={classes.filterSelect}
                disableUnderline
              >
                <MenuItem value="">All Programmes</MenuItem>
                {programmes.map(p => (
                  <MenuItem key={p.id} value={String(p.id)}>{p.code || p.name}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl className={classes.filterControl}>
              <InputLabel className={classes.filterInputLabel}>Semester</InputLabel>
              <Select
                value={selectedSemester}
                onChange={this.handleSemesterFilter}
                className={classes.filterSelect}
                disableUnderline
              >
                <MenuItem value="">All Semesters</MenuItem>
                {semesters.map(s => (
                  <MenuItem key={s.id} value={String(s.id)}>{s.name || `Semester ${s.id}`}</MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl className={classes.filterControl}>
              <InputLabel className={classes.filterInputLabel}>Day</InputLabel>
              <Select value="" className={classes.filterSelect} disableUnderline>
                <MenuItem value="">All Days</MenuItem>
                {DAYS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl className={classes.filterControl}>
              <InputLabel className={classes.filterInputLabel}>Lecturer</InputLabel>
              <Select value="" className={classes.filterSelect} disableUnderline>
                <MenuItem value="">All Lecturers</MenuItem>
                {staff.map(s => <MenuItem key={s.id} value={String(s.id)}>{s.firstName} {s.lastName}</MenuItem>)}
              </Select>
            </FormControl>

            <FormControl className={classes.filterControl}>
              <InputLabel className={classes.filterInputLabel}>Venue</InputLabel>
              <Select value="" className={classes.filterSelect} disableUnderline>
                <MenuItem value="">All Venues</MenuItem>
              </Select>
            </FormControl>

            <button className={classes.moreFiltersBtn}>
              <Icon style={{ fontSize: "20px" }}>filter_list</Icon>
              More Filters
            </button>
          </div>

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div className={classes.tagRow}>
              {currentSem && (
                <div className={classes.filterTag}>
                  {currentSem.name || `Semester ${currentSem.id}`}
                  <Icon style={{ fontSize: "14px", cursor: "pointer" }} onClick={() => this.setState({ selectedSemester: "" }, this.fetchEntries)}>close</Icon>
                </div>
              )}
              {currentProg && (
                <div className={classes.filterTag}>
                  {currentProg.code || currentProg.name}
                  <Icon style={{ fontSize: "14px", cursor: "pointer" }} onClick={() => this.setState({ selectedProgramme: "" }, this.fetchEntries)}>close</Icon>
                </div>
              )}
              {(selectedSemester || selectedProgramme) && (
                <span className={classes.clearAll} onClick={() => this.setState({ selectedSemester: "", selectedProgramme: "" }, this.fetchEntries)}>Clear all</span>
              )}
            </div>

            <div className={classes.toolbarRight}>
              <div className={classes.toggleBtnGroup}>
                <button className={`${classes.toggleBtn} ${classes.toggleBtnActive}`}>
                  <Icon style={{ fontSize: "18px" }}>view_list</Icon> Table View
                </button>
                <button className={`${classes.toggleBtn} ${classes.toggleBtnInactive}`}>
                  <Icon style={{ fontSize: "18px" }}>calendar_today</Icon> Calendar View
                </button>
              </div>
              <button className={classes.actionBtn}>
                <Icon style={{ fontSize: "18px" }}>swap_vert</Icon> Sort
              </button>
              <button className={classes.actionBtn}>
                <Icon style={{ fontSize: "18px" }}>file_upload</Icon> Export <Icon>expand_more</Icon>
              </button>
            </div>
          </div>
        </div>

        {error && <p className={classes.errorMsg}>{error}</p>}

        <DataTable
          columns={columns}
          data={entries}
          isLoading={loading}
          emptyState="No timetable entries found."
          searchValue={this.state.searchValue}
        />

        {/* Add/Edit Dialog */}
        <Dialog open={open} onClose={() => this.setState({ open: false })} maxWidth="sm" fullWidth>
          <DialogHeader title={editing ? "Edit Timetable Entry" : "Add Timetable Entry"} />
          <DialogContent>
            {formError && <p className={classes.errorMsg}>{formError}</p>}
            <GridContainer>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Programme *</InputLabel>
                  <Select value={form.programmeId} onChange={this.handleFormProgrammeChange}>
                    <MenuItem value="">— Select Programme —</MenuItem>
                    {programmes.map(p => (
                      <MenuItem key={p.id} value={String(p.id)}>{p.code ? `${p.code} — ${p.name}` : p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Semester</InputLabel>
                  <Select value={form.semesterId} onChange={this.handleChange("semesterId")}>
                    <MenuItem value="">— Select Semester —</MenuItem>
                    {semesters.map(s => (
                      <MenuItem key={s.id} value={String(s.id)}>{s.name || `Semester ${s.id}`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Day *</InputLabel>
                  <Select value={form.day} onChange={this.handleChange("day")}>
                    {DAYS.map(d => <MenuItem key={d} value={d}>{d}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={3}>
                <CustomDatePicker
                  dateFormat={false} timeFormat="HH:mm"
                  labelText="Start Time *"
                  id="tbStart"
                  formControlProps={{ fullWidth: true, style: { marginTop: "16px" } }}
                  inputProps={{ value: form.startTime, onChange: this.handleChange("startTime") }}
                />
              </GridItem>
              <GridItem xs={12} sm={3}>
                <CustomDatePicker
                  dateFormat={false} timeFormat="HH:mm"
                  labelText="End Time *"
                  id="tbEnd"
                  formControlProps={{ fullWidth: true, style: { marginTop: "16px" } }}
                  inputProps={{ value: form.endTime, onChange: this.handleChange("endTime") }}
                />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Course *</InputLabel>
                  <Select value={form.courseId} onChange={this.handleChange("courseId")} disabled={!form.programmeId}>
                    <MenuItem value="">{form.programmeId ? "— Select Course —" : "Select a programme first"}</MenuItem>
                    {dialogCourses.map(c => (
                      <MenuItem key={c.id} value={String(c.id)}>
                        {c.code ? `${c.code} — ` : ""}{c.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Lecturer</InputLabel>
                  <Select value={form.staffId} onChange={this.handleChange("staffId")}>
                    <MenuItem value="">— Select Lecturer —</MenuItem>
                    {staff.map(s => (
                      <MenuItem key={s.id} value={String(s.id)}>
                        {`${s.firstName || ""} ${s.lastName || ""}`.trim() || s.staffNo}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomInput
                  labelText="Venue"
                  id="tbVenue"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.venue, onChange: this.handleChange("venue"), placeholder: "e.g. Room A1" }}
                />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Type</InputLabel>
                  <Select value={form.type} onChange={this.handleChange("type")}>
                    {ENTRY_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
            </GridContainer>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ open: false })} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleSave} disabled={saving}>
              {saving ? <CircularProgress size={18} style={{ color: "#fff" }} /> : (editing ? "Update" : "Add Entry")}
            </Button>
          </DialogFooter>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={!!confirmDeleteId} onClose={() => this.setState({ confirmDeleteId: null })} maxWidth="xs" fullWidth>
          <DialogHeader title="Confirm Delete" />
          <DialogContent>
            <p>Are you sure you want to delete this timetable entry? This cannot be undone.</p>
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ confirmDeleteId: null })}>Cancel</Button>
            <Button color="danger" onClick={() => this.handleDelete(confirmDeleteId)} style={{ background: "#c62828", color: "#fff" }}>
              Delete
            </Button>
          </DialogFooter>
        </Dialog>

        {/* Auto-Generate Dialog */}
        <Dialog
          open={generateOpen}
          onClose={() => this.setState({ generateOpen: false })}
          maxWidth="sm"
          fullWidth
        >
          <DialogHeader title="Auto-Generate Timetable" />
          <DialogContent>
            <p style={{ fontSize: "13px", color: "#666", marginTop: 0 }}>
              Greedily fills each active course in the chosen programme &amp; semester into the first open
              day/hour with no clash for its lecturer, the programme's students, or a venue. Review the
              preview before saving — any sessions that couldn't be placed will be listed.
            </p>
            {generateError && <p className={classes.errorMsg}>{generateError}</p>}
            <GridContainer>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Programme *</InputLabel>
                  <Select value={generateForm.programmeId} onChange={this.handleGenerateChange("programmeId")}>
                    <MenuItem value="">— Select Programme —</MenuItem>
                    {programmes.map(p => (
                      <MenuItem key={p.id} value={String(p.id)}>{p.code ? `${p.code} — ${p.name}` : p.name}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Semester *</InputLabel>
                  <Select value={generateForm.semesterId} onChange={this.handleGenerateChange("semesterId")}>
                    <MenuItem value="">— Select Semester —</MenuItem>
                    {semesters.map(s => (
                      <MenuItem key={s.id} value={String(s.id)}>{s.name || `Semester ${s.id}`}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomInput
                  labelText="Day starts at (24h)"
                  id="genDayStart"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ type: "number", value: generateForm.dayStart, onChange: this.handleGenerateChange("dayStart") }}
                />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomInput
                  labelText="Day ends at (24h)"
                  id="genDayEnd"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ type: "number", value: generateForm.dayEnd, onChange: this.handleGenerateChange("dayEnd") }}
                />
              </GridItem>
              <GridItem xs={12}>
                <CustomInput
                  labelText="Venues (comma-separated)"
                  id="genVenues"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: generateForm.venues, onChange: this.handleGenerateChange("venues") }}
                />
              </GridItem>
            </GridContainer>

            {generatePreview && (
              <div style={{ marginTop: "20px" }}>
                <h5 style={{ margin: "0 0 8px" }}>
                  Preview — {generatePreview.entries.length} session{generatePreview.entries.length !== 1 ? "s" : ""} placed
                </h5>
                {generatePreview.unplaced.length > 0 && (
                  <p className={classes.errorMsg}>
                    {generatePreview.unplaced.length} session(s) could not be placed (no free slot for the lecturer, venue, or timeframe):{" "}
                    {generatePreview.unplaced.map(u => u.courseName).join(", ")}
                  </p>
                )}
                {generatePreview.alreadyScheduled.length > 0 && (
                  <p style={{ fontSize: "12px", color: "#888" }}>
                    Already has entries this semester — topped up instead of duplicated:{" "}
                    {generatePreview.alreadyScheduled
                      .map(a => `${a.courseName} (${a.existingSessions} existing, ${a.added} added)`)
                      .join(", ")}
                  </p>
                )}
                <div style={{ maxHeight: "260px", overflowY: "auto", border: "1px solid #eee", borderRadius: "4px" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "12px" }}>
                    <thead>
                      <tr style={{ background: "#f5f5f5" }}>
                        <th style={{ textAlign: "left", padding: "6px 8px" }}>Course</th>
                        <th style={{ textAlign: "left", padding: "6px 8px" }}>Day</th>
                        <th style={{ textAlign: "left", padding: "6px 8px" }}>Time</th>
                        <th style={{ textAlign: "left", padding: "6px 8px" }}>Lecturer</th>
                        <th style={{ textAlign: "left", padding: "6px 8px" }}>Venue</th>
                      </tr>
                    </thead>
                    <tbody>
                      {generatePreview.entries.map((e, idx) => (
                        <tr key={idx} style={{ borderTop: "1px solid #eee" }}>
                          <td style={{ padding: "6px 8px" }}>{this.getCourseLabel(e.courseId)}</td>
                          <td style={{ padding: "6px 8px" }}>{e.dayOfWeek}</td>
                          <td style={{ padding: "6px 8px" }}>{e.startTime} - {e.endTime}</td>
                          <td style={{ padding: "6px 8px" }}>{e.staffId ? this.getStaffLabel(e.staffId) : "—"}</td>
                          <td style={{ padding: "6px 8px" }}>{e.venue}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </DialogContent>
          <DialogFooter>
            <Button outlined onClick={() => this.setState({ generateOpen: false })} disabled={generating}>Cancel</Button>
            <Button color="info" onClick={() => this.runGenerate(false)} disabled={generating}>
              {generating ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Preview"}
            </Button>
            <Button
              color="primary"
              onClick={() => this.runGenerate(true)}
              disabled={generating || !generatePreview || generatePreview.entries.length === 0}
            >
              {generating ? <CircularProgress size={18} style={{ color: "#fff" }} /> : "Save Timetable"}
            </Button>
          </DialogFooter>
        </Dialog>
      </div>
    );
  }
}

TimetableBuilder.propTypes = { classes: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(TimetableBuilder));
